/**
 * Crafting: recipes, matching, and moving items in and out of the grid.
 *
 * Pure rules over an Inventory, like the stacking logic - the UI draws what
 * this says and calls back into it, so the fiddly parts (shaped patterns
 * matching anywhere in the grid, one item consumed per slot rather than per
 * stack) are testable without a browser.
 */

import {
  BlockId, WOOD_STAIRS_BASE, STONE_STAIRS_BASE,
  FENCE, gateId,
} from "../block/registry.js";
import { Inventory, type ItemStack } from "../inventory/inventory.js";
import { PlainItemId, itemLabel, itemMaxStack } from "../block/items.js";
import { pick, type Lang, type Phrase } from "../text/lang.js";
import { TREBUCHET_BASE } from "../block/registry.js";

/** The crafting grid is 3x3, available from the inventory with no table. */
export const GRID_SIZE = 3;
export const GRID_SLOTS = GRID_SIZE * GRID_SIZE;

/**
 * What a recipe slot will accept: one exact item, or any member of a tag.
 *
 * Tags exist because there are two kinds of plank and a stick does not care
 * which. Without them, every recipe taking wood would need writing out once
 * per species, and that count multiplies with every tree added.
 */
export type Ingredient = number | { tag: string };

/**
 * A place a recipe has to be worked at.
 *
 * Only one so far. Firing clay into brick and sand into glass needs a fire,
 * and until now the game simply pretended otherwise.
 */
export type Station = "stove" | "vat" | "wheel";

export const STATION_LABELS: Readonly<Record<Station, Phrase>> = {
  stove: { uk: "піч", en: "stove" },
  vat: { uk: "чан із водою", en: "vat of water" },
  wheel: { uk: "прядка", en: "spinning wheel" },
};

export const TAGS: Readonly<Record<string, readonly number[]>> = {
  planks: [BlockId.OakPlanks, BlockId.AcaciaPlanks, BlockId.PinePlanks],
  logs: [BlockId.OakLog, BlockId.AcaciaLog, BlockId.PineLog],
};

/** The concrete items an ingredient accepts. */
export function ingredientItems(ing: Ingredient): readonly number[] {
  return typeof ing === "number" ? [ing] : TAGS[ing.tag] ?? [];
}

export function ingredientMatches(itemId: number, ing: Ingredient): boolean {
  return typeof ing === "number"
    ? itemId === ing
    : (TAGS[ing.tag] ?? []).includes(itemId);
}

/** What to show in the recipe book: the tag's own name, or the item's. */
export function ingredientLabel(ing: Ingredient, lang: Lang = "uk"): string {
  if (typeof ing === "number") return itemLabel(ing, lang);
  const tag = TAG_LABELS[ing.tag];
  return tag === undefined ? ing.tag : pick(tag, lang);
}

const TAG_LABELS: Readonly<Record<string, Phrase>> = {
  planks: { uk: "будь-які дошки", en: "any boards" },
  logs: { uk: "будь-яка колода", en: "any log" },
};

interface RecipeBase {
  id: string;
  /** Where it must be worked. Undefined means anywhere. */
  requires?: Station;
  /**
   * The only map it can be made on. Undefined means all of them.
   *
   * "Поки він має бути доступний тільки на мапі «Сучасна війна»" - the танк
   * and its shells. A gate on the *recipe* rather than on the item: the
   * thing itself is an ordinary object that would work anywhere, and if it
   * is ever wanted elsewhere this line is what changes.
   */
  onlyOn?: string;
  note?: Phrase;
}

export interface ShapedRecipe extends RecipeBase {
  kind: "shaped";
  /**
   * Rows of the pattern, top to bottom. A space means "must be empty".
   * The pattern may sit anywhere in the grid, as long as its shape is intact.
   */
  pattern: readonly string[];
  /** Values are item ids, or a tag accepting any of several items. */
  key: Readonly<Record<string, Ingredient>>;
  result: ItemStack;
}

export interface ShapelessRecipe extends RecipeBase {
  kind: "shapeless";
  /** `count` is a number of occupied slots, not a stack size. */
  ingredients: readonly { id: number; count: number }[];
  result: ItemStack;
}

export type Recipe = ShapedRecipe | ShapelessRecipe;

/**
 * Everything craftable.
 *
 * Deliberately small and thematic rather than broad: straw off the steppe,
 * fired brick, clay-and-straw daub for хата walls, and black earth composted
 * out of soil and straw - which is the one recipe that is also true.
 */
export const RECIPES: readonly Recipe[] = [
  {
    id: "sticks",
    kind: "shaped",
    pattern: ["X", "X"],
    key: { X: { tag: "planks" } },
    result: { id: PlainItemId.Stick, count: 4 },
    note: { uk: "Дві дошки одна над одною.", en: "Two boards, one above the other." },
  },
  /*
   * Тин: rails between posts, which is what the shape says.
   *
   * Two rails of stick across two boards, the same three-wide shape a fence
   * has anywhere - and it yields three, because a fence is a thing you make
   * a run of rather than one of.
   */
  {
    id: "fence",
    kind: "shaped",
    pattern: ["XYX", "XYX"],
    key: { X: { tag: "planks" }, Y: PlainItemId.Stick },
    /*
     * Ten, and it was three.
     *
     * Four boards and two sticks for three blocks of fence was dear enough
     * that fencing a yard was a morning at the bench rather than a morning
     * in the yard - "рецепт тину досить дорогий". A тин is not carpentry:
     * it is willow bent through stakes, the cheapest wall there is, and the
     * cost should say so.
     */
    result: { id: FENCE, count: 10 },
    note: {
      uk: "Дошки по краях, палиці посередині — і десять кроків тину.",
      en: "Boards at the sides, sticks down the middle - and ten paces of fence.",
    },
  },
  /* Хвіртка: the same run of rails, with the posts on the outside. */
  {
    id: "gate",
    kind: "shaped",
    pattern: ["YXY", "YXY"],
    key: { X: { tag: "planks" }, Y: PlainItemId.Stick },
    result: { id: gateId(0, false), count: 1 },
    note: {
      uk: "Палиці по краях, дошки посередині — хвіртка в тин.",
      en: "Sticks at the sides, boards down the middle - a gate for a fence.",
    },
  },
  {
    id: "wooden_hoe",
    kind: "shaped",
    // Blade across the top, shaft down one side - the shape of the tool.
    pattern: ["XX", "Y ", "Y "],
    key: { X: { tag: "planks" }, Y: PlainItemId.Stick },
    result: { id: PlainItemId.WoodenHoe, count: 1 },
    note: { uk: "Щоб розорати землю під пшеницю.", en: "For breaking ground to sow wheat." },
  },
  {
    id: "wooden_pickaxe",
    kind: "shaped",
    // Head across the top, shaft down the middle.
    pattern: ["XXX", " Y ", " Y "],
    key: { X: { tag: "planks" }, Y: PlainItemId.Stick },
    result: { id: PlainItemId.WoodenPickaxe, count: 1 },
    note: { uk: "Без кайла камінь не візьмеш. Перше — дерев'яне.", en: "No stone without a pickaxe. The first one is wooden." },
  },
  {
    id: "wooden_axe",
    kind: "shaped",
    /*
     * Head on one side of the haft, not across the top of it.
     *
     * That is what separates it from the кайло on a bench: the pick is
     * "XXX" over a shaft down the middle, and a сокира is a wedge hung off
     * one side. The shape of the recipe is the shape of the tool, which is
     * the only way anybody remembers these.
     */
    pattern: ["XX", "XY", " Y"],
    key: { X: { tag: "planks" }, Y: PlainItemId.Stick },
    result: { id: PlainItemId.WoodenAxe, count: 1 },
    note: {
      uk: "Дерево рубати. Втричі швидше за руки.",
      en: "For chopping wood. Three times faster than bare hands.",
    },
  },
  {
    id: "wooden_shovel",
    kind: "shaped",
    pattern: ["X", "Y", "Y"],
    key: { X: { tag: "planks" }, Y: PlainItemId.Stick },
    result: { id: PlainItemId.WoodenShovel, count: 1 },
    note: { uk: "Копати глину з дна озера.", en: "For digging clay from a lake bed." },
  },
  {
    id: "wooden_sabre",
    kind: "shaped",
    pattern: ["X", "X", "Y"],
    key: { X: { tag: "planks" }, Y: PlainItemId.Stick },
    result: { id: PlainItemId.WoodenSabre, count: 1 },
    note: { uk: "Тренувальна. Справжню скуємо, як буде залізо.", en: "For practice. We forge the real one when there is iron." },
  },
  {
    id: "bow",
    kind: "shaped",
    // The stave down one side, the string down the other.
    pattern: [" XY", "X Y", " XY"],
    key: { X: PlainItemId.Stick, Y: PlainItemId.Fibre },
    result: { id: PlainItemId.Bow, count: 1 },
    note: { uk: "Тятива з лляного волокна.", en: "The string is flax fibre." },
  },
  /*
   * Кресало: the steel bent round, and the flint to strike it on.
   *
   * A stone and an ingot, in the shape the tile draws - the striker above,
   * the flint under it. It costs iron on purpose: fire is worth smelting for.
   */
  {
    id: "kresalo",
    kind: "shaped",
    pattern: ["XX", "Y "],
    key: { X: PlainItemId.IronIngot, Y: BlockId.Stone },
    result: { id: PlainItemId.Kresalo, count: 1 },
    note: {
      uk: "Криця й кремінь. Підпалює те, що горить.",
      en: "Steel and flint. Lights whatever will catch.",
    },
  },
  /*
   * Човен: five boards in the shape of the thing itself.
   *
   * The trough, seen from the end - two sides and a bottom. There are only so
   * many ways to draw a hull in a three-by-three grid, so the shape is the
   * obvious one; what makes it a човен rather than a boat is what it turns
   * into, which is a довбанка - a hollowed log, flat-bottomed, the thing that
   * has been on every river in this country for a thousand years.
   */
  {
    id: "choven",
    kind: "shaped",
    pattern: ["X X", "XXX"],
    key: { X: { tag: "planks" } },
    result: { id: PlainItemId.Choven, count: 1 },
    note: {
      uk: "Довбанка з дощок. Спускається тільки на воду.",
      en: "A dugout, in boards. Goes on water and nowhere else.",
    },
  },
  {
    id: "fishing_rod",
    kind: "shaped",
    // The rod up the diagonal and the line hanging off its tip, which is what
    // a вудка looks like and is a shape nothing else in the book uses.
    pattern: ["  X", " XY", "X Y"],
    key: { X: PlainItemId.Stick, Y: PlainItemId.Fibre },
    result: { id: PlainItemId.FishingRod, count: 1 },
    note: { uk: "Волосінь із лляного волокна. Ловиться карась.", en: "A flax line. Catches crucian." },
  },
  {
    id: "arrows",
    kind: "shaped",
    // Head, shaft, fletching - in that order, top to bottom.
    pattern: ["X", "Y", "Z"],
    key: {
      X: BlockId.Cobble,
      Y: PlainItemId.Stick,
      Z: BlockId.Thatch,
    },
    result: { id: PlainItemId.Arrow, count: 4 },
    note: { uk: "Кам'яний наконечник, держак, солом'яне оперення.", en: "A stone head, a shaft, straw fletching." },
  },
  {
    id: "mazanka_wall",
    kind: "shaped",
    // Three blocks into six halves: the same daub, spread thinner.
    pattern: ["XXX"],
    key: { X: BlockId.Mazanka },
    result: { id: PlainItemId.MazankaWall, count: 6 },
    note: { uk: "Три мазанки — шість тонких стін.", en: "Three blocks of daub make six thin walls." },
  },
  {
    id: "granite_wall",
    kind: "shaped",
    // The same three-into-six as the daub, in stone that has to be cut.
    pattern: ["XXX"],
    key: { X: BlockId.Granite },
    result: { id: PlainItemId.GraniteWall, count: 6 },
    note: { uk: "Три граніти — шість гранітних стін.",
      en: "Three blocks of granite make six granite walls." },
  },
  {
    id: "mazanka_slab",
    kind: "shaped",
    /*
     * Two blocks into four halves, which is what halving two blocks is.
     *
     * Not three into six, which is the wall's pattern exactly - and two
     * shaped recipes with the same squares and the same material are one
     * recipe as far as the grid is concerned. A check said so before either
     * of them ever reached a player: "mazanka_slab: mazanka_wall".
     */
    pattern: ["XX"],
    key: { X: BlockId.Mazanka },
    result: { id: PlainItemId.MazankaSlab, count: 4 },
    note: { uk: "Дві мазанки — чотири плити у пів блока.",
      en: "Two blocks of daub make four half-blocks." },
  },
  {
    id: "granite_slab",
    kind: "shaped",
    pattern: ["XX"],
    key: { X: BlockId.Granite },
    result: { id: PlainItemId.GraniteSlab, count: 4 },
    note: { uk: "Два граніти — чотири плити у пів блока.",
      en: "Two blocks of granite make four half-blocks." },
  },
  {
    id: "chest",
    kind: "shaped",
    // Eight boards round a hollow - which is what a box is. The stove is the
    // same shape in clay; the material is what tells them apart.
    pattern: ["XXX", "X X", "XXX"],
    key: { X: { tag: "planks" } },
    result: { id: PlainItemId.Chest, count: 1 },
    note: { uk: "Вісім дощок навколо порожнечі. Тримає 27 стосів.", en: "Eight boards round a hollow. Holds 27 stacks." },
  },
  {
    id: "oak_planks",
    kind: "shapeless",
    ingredients: [{ id: BlockId.OakLog, count: 1 }],
    result: { id: BlockId.OakPlanks, count: 4 },
    note: { uk: "Одна колода — чотири дошки.", en: "One log, four boards." },
  },
  {
    id: "pine_planks",
    kind: "shapeless",
    ingredients: [{ id: BlockId.PineLog, count: 1 }],
    result: { id: BlockId.PinePlanks, count: 4 },
    note: { uk: "Одна колода — чотири дошки.", en: "One log, four boards." },
  },
  {
    id: "blueprint",
    kind: "shaped",
    // The recipe is a picture of what it builds: a thatched roof over daub
    // walls with a door in them, standing on a plank floor. Whoever can make
    // all three can already build a хата by hand - this saves the afternoon,
    // not the learning.
    pattern: ["TTT", "MDM", "PPP"],
    key: {
      T: BlockId.Thatch,
      M: BlockId.Mazanka,
      D: PlainItemId.OakDoor,
      P: { tag: "planks" },
    },
    result: { id: BlockId.Blueprint, count: 1 },
    note: { uk: "Ставиш — і хата стає позаду каменя.", en: "Put it down and a хата stands up behind it." },
  },
  {
    id: "torch",
    kind: "shaped",
    // Rag over resin over the stick, in the order they go onto it: the смола
    // soaks the cloth and the cloth is bound to the stick.
    pattern: ["Y", "Z", "X"],
    key: {
      X: PlainItemId.Stick,
      Y: PlainItemId.Linen,
      Z: PlainItemId.Resin,
    },
    result: { id: BlockId.Torch, count: 4 },
    note: { uk: "Держак, лляна ганчірка і соснова смола.", en: "A shaft, a rag of linen and pine resin." },
  },
  /*
   * The same смолоскип, wrapped in a вовкулака instead of in linen.
   *
   * Its own recipe rather than a tag over the two, because they are not the
   * same kind of thing: линen is grown, spun and woven over a season, and a
   * hide is what is left after something tried to kill you. A player who has
   * one has had a different evening from a player who has the other, and the
   * book should show both.
   */
  {
    id: "torch_hide",
    kind: "shaped",
    pattern: ["Y", "Z", "X"],
    key: {
      X: PlainItemId.Stick,
      Y: PlainItemId.VovkulakaHide,
      Z: PlainItemId.Resin,
    },
    result: { id: BlockId.Torch, count: 4 },
    note: {
      uk: "Держак, шкура вовкулаки і смола. Горить не гірше за полотно.",
      en: "A shaft, a werewolf's hide and resin. Burns as well as linen.",
    },
  },
  {
    id: "acacia_planks",
    kind: "shapeless",
    ingredients: [{ id: BlockId.AcaciaLog, count: 1 }],
    result: { id: BlockId.AcaciaPlanks, count: 4 },
  },
  {
    id: "stove",
    kind: "shaped",
    /*
     * Raw clay, not brick.
     *
     * Brick needs firing and firing needs a stove, so a stove made of brick
     * could never be built at all. A real піч is raised from clay by hand and
     * hardens with use, which is both the way out of that circle and the
     * truthful answer.
     */
    pattern: ["XXX", "X X", "XXX"],
    key: { X: BlockId.Clay },
    result: { id: PlainItemId.Stove, count: 1 },
    note: { uk: "Вісім грудок глини навколо порожнечі. Печі не треба.", en: "Eight lumps of clay round a hollow. No stove needed." },
  },
  {
    id: "stove_limed",
    kind: "shapeless",
    // The whitewash comes from the same daub the walls are limed with.
    ingredients: [
      { id: PlainItemId.Stove, count: 1 },
      { id: BlockId.Mazanka, count: 1 },
    ],
    result: { id: PlainItemId.StoveLimed, count: 1 },
    note: { uk: "Побілити вапном, як стіни.", en: "Limewashed, like the walls." },
  },
  {
    id: "clay_bucket",
    kind: "shaped",
    // A pail: two sides and a base.
    pattern: ["X X", " X "],
    key: { X: BlockId.Clay },
    result: { id: PlainItemId.ClayBucket, count: 1 },
    requires: "stove",
    note: { uk: "Випалене в печі. Носити воду.", en: "Fired in a stove. For carrying water." },
  },
  {
    id: "vat",
    kind: "shaped",
    // Wider and deeper than the pail, and it takes more clay to match.
    pattern: ["X X", "XXX"],
    key: { X: BlockId.Clay },
    result: { id: PlainItemId.Vat, count: 1 },
    requires: "stove",
    note: { uk: "Велика посудина. Для вимочування льону.", en: "A big vessel. For retting flax." },
  },
  {
    id: "glass",
    kind: "shapeless",
    /*
     * Sand plus straw to fire it.
     *
     * Four sand alone was already пісковик, and two recipes with the same
     * ingredients means the second is unreachable - the first to match always
     * wins. The straw is the fuel: this predates the stove having a fire of
     * its own, and it stays because shaping and firing clay at a stove is one
     * motion, while smelting is the slow thing you leave burning. Firing sand
     * over a straw fire is also simply what a пічка did.
     */
    ingredients: [
      { id: BlockId.Sand, count: 3 },
      { id: BlockId.Thatch, count: 1 },
    ],
    result: { id: BlockId.Glass, count: 3 },
    requires: "stove",
    note: { uk: "Пісок, перепалений на соломі. Потрібна піч.", en: "Sand burnt over straw. Needs a stove." },
  },
  {
    id: "oak_window",
    kind: "shaped",
    // The shape is the thing itself: a frame of boards around one pane.
    pattern: ["XXX", "XZX", "XXX"],
    key: { X: BlockId.OakPlanks, Z: BlockId.Glass },
    result: { id: BlockId.OakWindow, count: 2 },
    note: { uk: "Скло в дубовій рамі.", en: "Glass in an oak frame." },
  },
  {
    id: "acacia_window",
    kind: "shaped",
    pattern: ["XXX", "XZX", "XXX"],
    key: { X: BlockId.AcaciaPlanks, Z: BlockId.Glass },
    result: { id: BlockId.AcaciaWindow, count: 2 },
    note: { uk: "Скло в рамі з акації.", en: "Glass in an acacia frame." },
  },
  {
    id: "fibre",
    kind: "shapeless",
    // Retting: the stalks soak until the woody part lets go of the fibre.
    ingredients: [{ id: PlainItemId.FlaxStalks, count: 3 }],
    result: { id: PlainItemId.Fibre, count: 2 },
    requires: "vat",
    note: { uk: "Вимочити стебла. Потрібен чан із водою.", en: "Ret the stalks. Needs a vat of water." },
  },
  {
    id: "spinning_wheel",
    kind: "shaped",
    pattern: ["XYX", "YYY"],
    key: { X: PlainItemId.Stick, Y: { tag: "planks" } },
    result: { id: PlainItemId.SpinningWheel, count: 1 },
    note: { uk: "Прясти волокно.", en: "Spin the fibre." },
  },
  {
    id: "linen",
    kind: "shapeless",
    ingredients: [{ id: PlainItemId.Fibre, count: 3 }],
    result: { id: PlainItemId.Linen, count: 1 },
    requires: "wheel",
    note: { uk: "Спрясти й виткати. Потрібна прядка.", en: "Spun and woven. Needs a spinning wheel." },
  },
  {
    id: "rushnyk",
    kind: "shaped",
    pattern: ["XXX"],
    key: { X: PlainItemId.Linen },
    result: { id: PlainItemId.Rushnyk, count: 1 },
    note: { uk: "Вишитий рушник на стіну.", en: "An embroidered rushnyk for the wall." },
  },
  {
    id: "bed",
    kind: "shaped",
    // A straw tick on a plank frame - what a хата bed actually was.
    pattern: ["XXX", "YYY"],
    key: { X: BlockId.Thatch, Y: { tag: "planks" } },
    result: { id: PlainItemId.Bed, count: 1 },
    note: { uk: "Солом'яник на дошках. Ставить точку відродження.", en: "A straw mattress on boards. Sets where you wake." },
  },
  {
    id: "oak_shutter",
    kind: "shaped",
    pattern: ["XX", "XX"],
    key: { X: BlockId.OakPlanks },
    result: { id: PlainItemId.OakShutter, count: 2 },
    note: { uk: "Стулки на вікно, з дуба.", en: "Window leaves, in oak." },
  },
  {
    id: "acacia_shutter",
    kind: "shaped",
    pattern: ["XX", "XX"],
    key: { X: BlockId.AcaciaPlanks },
    result: { id: PlainItemId.AcaciaShutter, count: 2 },
    note: { uk: "Стулки на вікно, з акації.", en: "Window leaves, in acacia." },
  },
  {
    id: "oak_door",
    kind: "shaped",
    // Two boards wide and three tall - the shape of the door itself.
    pattern: ["XX", "XX", "XX"],
    key: { X: BlockId.OakPlanks },
    result: { id: PlainItemId.OakDoor, count: 2 },
    note: { uk: "Двері з дуба - темні й важкі.", en: "An oak door - dark and heavy." },
  },
  {
    id: "acacia_door",
    kind: "shaped",
    pattern: ["XX", "XX", "XX"],
    key: { X: BlockId.AcaciaPlanks },
    result: { id: PlainItemId.AcaciaDoor, count: 2 },
    note: { uk: "Двері з акації - світлі, з філянками.", en: "An acacia door - pale, with panels." },
  },
  {
    id: "thatch",
    kind: "shapeless",
    ingredients: [{ id: BlockId.Grass, count: 2 }],
    result: { id: BlockId.Thatch, count: 1 },
    note: { uk: "Суха трава на стріху.", en: "Dry grass for a roof." },
  },
  {
    id: "chornozem",
    kind: "shapeless",
    ingredients: [
      { id: BlockId.Dirt, count: 1 },
      { id: BlockId.Thatch, count: 1 },
    ],
    result: { id: BlockId.Chornozem, count: 2 },
    note: { uk: "Солома перепріває в землі — так і родиться чорнозем.", en: "Straw rots down into earth - that is how black soil is made." },
  },
  /*
   * The iron kit. All of it needs the піч standing by, which the wooden tools
   * do not: you can whittle a haft anywhere, and you cannot forge without a
   * fire. That also gives the stove a second life after smelting - it is the
   * forge as well as the furnace, which is what a village stove actually was.
   */
  {
    id: "iron_axe",
    kind: "shaped",
    // The same shape as the wooden one, in iron, for the same reason the two
    // picks share theirs.
    pattern: ["XX", "XY", " Y"],
    key: { X: PlainItemId.IronIngot, Y: PlainItemId.Stick },
    result: { id: PlainItemId.IronAxe, count: 1 },
    requires: "stove",
    note: {
      uk: "Криця. Колода з двох ударів. Кується біля печі.",
      en: "Steel. A log in two blows. Forged at the stove.",
    },
  },
  {
    id: "iron_pickaxe",
    kind: "shaped",
    // The same shape as the wooden one, in iron. A different pattern for the
    // same tool would be a thing to memorise for no reason.
    pattern: ["XXX", " Y ", " Y "],
    key: { X: PlainItemId.IronIngot, Y: PlainItemId.Stick },
    result: { id: PlainItemId.IronPickaxe, count: 1 },
    requires: "stove",
    note: { uk: "Те саме кайло, тільки з криці. Кується біля печі.", en: "The same pickaxe, in steel. Forged at the stove." },
  },
  /*
   * The cell, and the machine it flies.
   *
   * Manganese is not decoration here: it is what a dry cell is actually made
   * of, and the game already digs the Nikopol kind. The frame is four sticks
   * for arms and a steel body, which is the shape the picture shows.
   */
  {
    id: "battery",
    kind: "shaped",
    pattern: [" Y ", "XXX", "XXX"],
    key: { X: PlainItemId.Manganese, Y: PlainItemId.IronIngot },
    result: { id: PlainItemId.Battery, count: 1 },
    requires: "stove",
    note: { uk: "Марганець нікопольський. Те, з чого батарейку й роблять.",
      en: "Nikopol manganese. What a cell is actually made of." },
  },
  {
    id: "drone",
    kind: "shaped",
    pattern: ["Y Y", " Z ", "Y Y"],
    key: { Y: PlainItemId.Stick, Z: PlainItemId.Battery },
    result: { id: PlainItemId.Drone, count: 1 },
    requires: "stove",
    note: { uk: "Чотири промені й батарея посередині.",
      en: "Four arms and a cell in the middle." },
  },
  /*
   * The пульт: a box, a cell and an aerial.
   *
   * Cheaper than the drone and made of the same parts, because it is not the
   * clever half: losing the handset must not cost you the machine. Iron for
   * the case, a battery to run it, a stick for the aerial.
   */
  {
    id: "remote",
    kind: "shaped",
    pattern: [" Y ", "XZX", "XXX"],
    key: {
      X: PlainItemId.IronIngot, Y: PlainItemId.Stick, Z: PlainItemId.Battery,
    },
    result: { id: PlainItemId.Remote, count: 1 },
    requires: "stove",
    note: { uk: "Коробка, батарея і антена.",
      en: "A box, a cell and an aerial." },
  },
  /*
   * And putting a new one in.
   *
   * A flight costs a battery whether it lasted five minutes or five seconds -
   * a stack in the bag has no memory, so there is nowhere to keep "three
   * minutes left". Landing early wastes the rest, which is one line to change
   * if it turns out to be mean rather than a reason to commit to the sortie.
   */
  {
    id: "drone_recharge",
    kind: "shapeless",
    ingredients: [
      { id: PlainItemId.DroneFlat, count: 1 },
      { id: PlainItemId.Battery, count: 1 },
    ],
    result: { id: PlainItemId.Drone, count: 1 },
    note: { uk: "Нова батарея — і знову п'ять хвилин.",
      en: "A new cell, and five more minutes." },
  },
  /*
   * The танк and what it fires, on the one map that has either.
   *
   * "Його робим із заліза та нафти... набої мають бути нові та робитись із
   * пороху та заліза... додай гуму в рецепт на танк."
   */
  {
    id: "tank",
    kind: "shaped",
    // Iron over iron with a barrel of oil inside it, standing on rubber
    // tracks: the recipe is the drawing on the tile, which is how anybody
    // remembers one.
    pattern: ["III", "IOI", "RRR"],
    key: {
      I: PlainItemId.IronIngot,
      O: PlainItemId.CrudeOil,
      R: PlainItemId.Rubber,
    },
    result: { id: PlainItemId.Tank, count: 1 },
    onlyOn: "war",
    requires: "stove",
    note: {
      uk: "Залізо, нафта й гума. Кується біля печі.",
      en: "Iron, oil and rubber. Forged at the stove.",
    },
  },
  {
    id: "tank_shell",
    kind: "shaped",
    pattern: ["I", "P"],
    key: { I: PlainItemId.IronIngot, P: PlainItemId.Gunpowder },
    result: { id: PlainItemId.TankShell, count: 2 },
    onlyOn: "war",
    requires: "stove",
    note: {
      uk: "Порох знизу, залізо зверху. Двічі за раз.",
      en: "Powder under iron. Two at a time.",
    },
  },
  {
    id: "adamant_pickaxe",
    kind: "shaped",
    // The same shape as the other two picks, in crystal.
    pattern: ["XXX", " Y ", " Y "],
    key: { X: BlockId.Adamant, Y: PlainItemId.Stick },
    result: { id: PlainItemId.AdamantPickaxe, count: 1 },
    requires: "stove",
    note: {
      uk: "Єдине, чим можна зламати прапор. Кується біля печі.",
      en: "The only thing that takes a flag down. Forged at the stove.",
    },
  },
  {
    id: "adamant_sabre",
    kind: "shaped",
    pattern: ["X", "X", "Y"],
    key: { X: BlockId.Adamant, Y: PlainItemId.Stick },
    result: { id: PlainItemId.AdamantSabre, count: 1 },
    requires: "stove",
    note: {
      uk: "Криця ламається об неї.",
      en: "Steel breaks against it.",
    },
  },
  {
    id: "iron_sabre",
    kind: "shaped",
    // The same shape as the training one, in steel.
    pattern: ["X", "X", "Y"],
    key: { X: PlainItemId.IronIngot, Y: PlainItemId.Stick },
    result: { id: PlainItemId.IronSabre, count: 1 },
    requires: "stove",
    note: { uk: "Справжня. Та, що обіцяла дерев'яна.", en: "The real one. What the wooden sabre promised." },
  },
  {
    id: "iron_helm",
    kind: "shaped",
    // Pointed: the spike is the шишак, and the pattern says so.
    pattern: [" X ", "XXX", "X X"],
    key: { X: PlainItemId.IronIngot },
    result: { id: PlainItemId.Helm, count: 1 },
    requires: "stove",
    note: { uk: "Шишак. Шпиль угорі, нащічники з боків.", en: "A helm. A spike on top, cheek guards at the sides." },
  },
  /*
   * Луска дракона, in the same four shapes as the iron kit.
   *
   * The patterns are deliberately identical to the iron ones - a шишак is a
   * шишак whatever it is beaten out of - so anybody who has made the iron
   * set already knows how to make this one. Twenty-four scales for the four
   * of them, which is exactly what one Змій drops.
   */
  {
    id: "scale_helm",
    kind: "shaped",
    pattern: [" X ", "XXX", "X X"],
    key: { X: PlainItemId.DragonScale },
    result: { id: PlainItemId.ScaleHelm, count: 1 },
    requires: "stove",
    note: { uk: "Шолом із луски. Той самий крій, що й шишак.",
      en: "A scale helm. The same cut as the iron one." },
  },
  {
    id: "scale_mail",
    kind: "shaped",
    pattern: ["X X", "XXX", "XXX"],
    key: { X: PlainItemId.DragonScale },
    result: { id: PlainItemId.ScaleMail, count: 1 },
    requires: "stove",
    note: { uk: "Панцир із луски. Боронить краще за кольчугу.",
      en: "Scale mail. It stops more than a mail shirt." },
  },
  {
    id: "scale_bracers",
    kind: "shaped",
    pattern: ["X X", "X X"],
    key: { X: PlainItemId.DragonScale },
    result: { id: PlainItemId.ScaleBracers, count: 1 },
    requires: "stove",
    note: { uk: "Наручі з луски.", en: "Scale bracers." },
  },
  {
    id: "scale_boots",
    kind: "shaped",
    pattern: ["X X", "XXX"],
    key: { X: PlainItemId.DragonScale },
    result: { id: PlainItemId.ScaleBoots, count: 1 },
    requires: "stove",
    note: { uk: "Чоботи з луски.", en: "Scale boots." },
  },
  {
    id: "iron_mail",
    kind: "shaped",
    pattern: ["X X", "XXX", "XXX"],
    key: { X: PlainItemId.IronIngot },
    result: { id: PlainItemId.Mail, count: 1 },
    requires: "stove",
    note: { uk: "Кольчуга. Найдорожча і найпотрібніша.", en: "Mail. The dearest and the most needed." },
  },
  {
    id: "iron_bracers",
    kind: "shaped",
    pattern: ["X X", "X X"],
    key: { X: PlainItemId.IronIngot },
    result: { id: PlainItemId.Bracers, count: 1 },
    requires: "stove",
    note: { uk: "Наручі на обидві руки.", en: "Bracers for both arms." },
  },
  {
    id: "iron_boots",
    kind: "shaped",
    // Uppers over soles, which is what makes it boots and not more наручі.
    pattern: ["X X", "XXX"],
    key: { X: PlainItemId.IronIngot },
    result: { id: PlainItemId.Boots, count: 1 },
    requires: "stove",
    note: { uk: "Чоботи з халявами.", en: "Boots with tall tops." },
  },
  {
    id: "brick",
    kind: "shaped",
    pattern: ["XX", "XX"],
    key: { X: BlockId.Clay },
    result: { id: BlockId.Brick, count: 4 },
    requires: "stove",
    note: { uk: "Випалена глина. Потрібна піч.", en: "Fired clay. Needs a stove." },
  },
  {
    id: "white_stone",
    kind: "shaped",
    // Stone set in lime: the same burnt lime the walls are whitewashed with,
    // which is why it needs a fire under it.
    pattern: ["XY", "YX"],
    key: { X: BlockId.Stone, Y: BlockId.Clay },
    result: { id: BlockId.WhiteStone, count: 4 },
    requires: "stove",
    note: { uk: "Тесаний камінь на вапні. Потрібна піч.", en: "Dressed stone set in lime. Needs a stove." },
  },
  {
    id: "green_tile",
    kind: "shaped",
    /*
     * Fired clay under a green glaze, the glaze being ash of лляні стебла.
     *
     * Leaves would have been the obvious green thing and they are not a
     * thing you can hold: a felled crown drops паростки, never leaves. The
     * recipe-reachability test caught that, which is the second time it has
     * paid for itself.
     */
    pattern: ["YY", "XX"],
    key: { X: BlockId.Clay, Y: PlainItemId.FlaxStalks },
    result: { id: BlockId.GreenTile, count: 4 },
    requires: "stove",
    note: { uk: "Глина під зеленою поливою. Потрібна піч.", en: "Clay under a green glaze. Needs a stove." },
  },
  {
    id: "firebolt",
    kind: "shaped",
    /*
     * A stone, wrapped in linen, soaked in смола.
     *
     * Exactly the three things a fire-pot is: the stone gives it the weight
     * to be thrown and to break what it lands on, the cloth carries the
     * flame, and the resin is what makes it burn rather than smoulder. The
     * same смола as a смолоскип, doing the same job on a larger scale.
     */
    pattern: [" L ", "LSL", " R "],
    key: {
      L: PlainItemId.Linen,
      S: BlockId.Stone,
      R: PlainItemId.Resin,
    },
    result: { id: PlainItemId.Firebolt, count: 2 },
    note: {
      uk: "Камінь у полотні, просочений смолою. Горить.",
      en: "A stone in linen, soaked in resin. It burns.",
    },
  },
  /*
   * Сходи, six of a thing into four of it.
   *
   * The staircase shape, laid out as it climbs, so the recipe reads as the
   * thing it makes. Four out of six rather than four out of four: a stair is
   * three quarters of a block and the arithmetic should not reward cutting
   * one up.
   */
  {
    id: "wood_stairs",
    kind: "shaped",
    pattern: ["P  ", "PP ", "PPP"],
    key: { P: { tag: "planks" } },
    result: { id: WOOD_STAIRS_BASE, count: 4 },
    note: {
      uk: "Дошки сходинками. Шість дощок - четверо сходів.",
      en: "Boards, cut as steps. Six boards make four stairs.",
    },
  },
  {
    id: "stone_stairs",
    kind: "shaped",
    pattern: ["S  ", "SS ", "SSS"],
    key: { S: BlockId.Stone },
    result: { id: STONE_STAIRS_BASE, count: 4 },
    note: {
      uk: "Камінь сходинками. Шість каменів - четверо сходів.",
      en: "Stone, cut as steps. Six stones make four stairs.",
    },
  },
  {
    id: "trebuchet",
    kind: "shaped",
    /*
     * Timber, rope and a counterweight.
     *
     * Any boards for the frame, лляне полотно for the rope - the same fibre
     * the bowstring is - and a block of stone hanging off the short arm,
     * which is the whole idea of the machine.
     */
    pattern: ["RPR", "PSP", "P P"],
    key: {
      P: { tag: "planks" },
      R: PlainItemId.Linen,
      S: BlockId.Stone,
    },
    result: { id: TREBUCHET_BASE, count: 1 },
    note: {
      uk: "Рама, канат і камінь-противага. Кидає снаряди.",
      en: "A frame, a rope and a stone counterweight. It throws.",
    },
  },
  {
    id: "kremlin_stone",
    kind: "shaped",
    /*
     * A bigger plan, drawn on a bigger stone.
     *
     * The хата stone is in the middle of it: you learn to raise a house
     * before you raise a fortress, and the recipe says so without a word of
     * explanation. The ring round it is what the кремль is actually built
     * from - brick, white stone and roof tile - which is the same trick the
     * хата stone's own recipe plays.
     */
    pattern: ["BWB", "GPG", "BWB"],
    key: {
      B: BlockId.Brick,
      W: BlockId.WhiteStone,
      G: BlockId.GreenTile,
      P: BlockId.Blueprint,
    },
    result: { id: BlockId.KremlinStone, count: 1 },
    note: { uk: "Ставиш — і кремль стає позаду каменя. Треба багато місця.", en: "Put it down and a кремль stands up behind it. It needs a lot of room." },
  },
  {
    id: "mazanka",
    kind: "shaped",
    // Clay and straw alternating - the daub a хата wall is actually made of.
    pattern: ["XY", "YX"],
    key: { X: BlockId.Clay, Y: BlockId.Thatch },
    result: { id: BlockId.Mazanka, count: 4 },
    note: { uk: "Глина з соломою, побілена вапном.", en: "Clay and straw, limewashed." },
  },
  {
    id: "sandstone",
    kind: "shaped",
    pattern: ["XX", "XX"],
    key: { X: BlockId.Sand },
    result: { id: BlockId.Sandstone, count: 4 },
  },
  {
    id: "cobble",
    kind: "shaped",
    pattern: ["XX", "XX"],
    key: { X: BlockId.Stone },
    result: { id: BlockId.Cobble, count: 4 },
    note: { uk: "Бруківка, як на старій площі.", en: "Cobbles, like an old square." },
  },
  {
    id: "stone_tile",
    kind: "shaped",
    pattern: ["XX", "XX"],
    key: { X: BlockId.Cobble },
    result: { id: BlockId.StoneTile, count: 4 },
    note: { uk: "Тесаний камінь, покладений рядами.", en: "Dressed stone, laid in courses." },
  },
];

/**
 * Every item any recipe asks for, worked out once.
 *
 * Tags are expanded: `{ tag: "planks" }` puts oak, pine and acacia in here
 * separately, because the question this answers is about a thing in somebody's
 * hand and a hand holds an item, not a tag.
 *
 * A Set built at load rather than a search per pickup. It is asked once per
 * new item, which is not often - but it is asked from the frame loop's side
 * of the fence, and a linear walk of every recipe's every ingredient there is
 * the kind of thing that is cheap until the recipe list is long.
 */
const WANTED: ReadonlySet<number> = (() => {
  const out = new Set<number>();
  for (const recipe of RECIPES) {
    if (recipe.kind === "shaped") {
      for (const ing of Object.values(recipe.key)) {
        for (const id of ingredientItems(ing)) out.add(id);
      }
    } else {
      for (const { id: ing } of recipe.ingredients) {
        for (const id of ingredientItems(ing)) out.add(id);
      }
    }
  }
  return out;
})();

/**
 * Is this item wanted by any recipe at all?
 *
 * What the book's nudge is asked: somebody has just picked up a thing they
 * have never held, and the question is whether the book has anything to say
 * about it. A лума of граніт is worth a look in the book; a сало is not.
 */
export function wantedByAnyRecipe(itemId: number): boolean {
  return WANTED.has(itemId);
}

/**
 * Which of these are new, and does the book know about any of them?
 *
 * The brain of the nudge in the corner, and it lives here rather than in
 * `main.ts` so it can be asked questions. What `main.ts` keeps is the Set and
 * the blinking; what is worth being sure of is this: a thing counts once, the
 * first time it is held, and only a thing some recipe wants counts at all.
 *
 * `seen` is **written to**: everything in the bag is marked as held, whether
 * or not the book wanted it. Otherwise a сало in slot 0 would be "new" every
 * frame for as long as it sat there, and the first plank picked up afterwards
 * would be the second thing reported rather than the first.
 */
export function noticeNewIngredients(
  slots: readonly (ItemStack | null)[],
  seen: Set<number>,
): number[] {
  const fresh: number[] = [];
  for (const slot of slots) {
    if (slot === null || seen.has(slot.id)) continue;
    seen.add(slot.id);
    if (wantedByAnyRecipe(slot.id)) fresh.push(slot.id);
  }
  return fresh;
}

export function recipeById(id: string): Recipe | undefined {
  return RECIPES.find((r) => r.id === id);
}

/** Human-readable name of what a recipe makes. */
export function recipeName(recipe: Recipe, lang: Lang = "uk"): string {
  return itemLabel(recipe.result.id, lang);
}

export interface Requirement {
  ingredient: Ingredient;
  /** Number of grid slots it must fill. */
  count: number;
}

/** What a recipe needs in total. */
export function recipeCost(recipe: Recipe): Requirement[] {
  const out: Requirement[] = [];
  const bump = (ingredient: Ingredient): void => {
    const key = JSON.stringify(ingredient);
    const found = out.find((r) => JSON.stringify(r.ingredient) === key);
    if (found === undefined) out.push({ ingredient, count: 1 });
    else found.count++;
  };

  if (recipe.kind === "shapeless") {
    for (const ing of recipe.ingredients) {
      for (let i = 0; i < ing.count; i++) bump(ing.id);
    }
  } else {
    for (const row of recipe.pattern) {
      for (const ch of row) {
        if (ch === " ") continue;
        const ing = recipe.key[ch];
        if (ing !== undefined) bump(ing);
      }
    }
  }
  return out;
}

/** Bounding box of the occupied cells, or null when the grid is empty. */
function occupiedBounds(
  grid: Inventory,
): { top: number; left: number; rows: number; cols: number } | null {
  let top = GRID_SIZE;
  let left = GRID_SIZE;
  let bottom = -1;
  let right = -1;

  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (grid.get(r * GRID_SIZE + c) === null) continue;
      if (r < top) top = r;
      if (r > bottom) bottom = r;
      if (c < left) left = c;
      if (c > right) right = c;
    }
  }
  if (bottom < 0) return null;
  return { top, left, rows: bottom - top + 1, cols: right - left + 1 };
}

function matchesShaped(recipe: ShapedRecipe, grid: Inventory): boolean {
  const bounds = occupiedBounds(grid);
  if (bounds === null) return false;

  const rows = recipe.pattern.length;
  const cols = Math.max(...recipe.pattern.map((r) => r.length));
  // The pattern may sit anywhere, but its shape must match exactly - a 2x2
  // recipe must not be satisfied by three items and a gap.
  if (bounds.rows !== rows || bounds.cols !== cols) return false;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const ch = recipe.pattern[r]?.[c] ?? " ";
      const slot = grid.get((bounds.top + r) * GRID_SIZE + (bounds.left + c));
      if (ch === " ") {
        if (slot !== null) return false;
        continue;
      }
      const ing = recipe.key[ch];
      if (slot === null || ing === undefined) return false;
      if (!ingredientMatches(slot.id, ing)) return false;
    }
  }
  return true;
}

function matchesShapeless(recipe: ShapelessRecipe, grid: Inventory): boolean {
  const have = new Map<number, number>();
  let occupied = 0;
  for (let i = 0; i < GRID_SLOTS; i++) {
    const slot = grid.get(i);
    if (slot === null) continue;
    occupied++;
    have.set(slot.id, (have.get(slot.id) ?? 0) + 1);
  }

  const needed = recipe.ingredients.reduce((sum, i) => sum + i.count, 0);
  // Anything extra in the grid must invalidate the match, or a stray block
  // would be silently eaten.
  if (occupied !== needed) return false;

  for (const ing of recipe.ingredients) {
    if ((have.get(ing.id) ?? 0) !== ing.count) return false;
  }
  return true;
}

/**
 * Is this recipe workable here?
 *
 * Deliberately separate from matching. A recipe you have laid out correctly
 * but cannot fire should show its result greyed out with a reason, not vanish
 * as though the arrangement were wrong - the player would rearrange it forever.
 */
export function recipeBlocked(
  recipe: Recipe, stations: ReadonlySet<Station>,
): Station | null {
  if (recipe.requires === undefined) return null;
  return stations.has(recipe.requires) ? null : recipe.requires;
}

/**
 * Is this recipe not for this map at all?
 *
 * Different from `recipeBlocked`, which greys a recipe out and says what is
 * missing. This one hides it: a танк on the кремль is not a thing you have
 * not found the workbench for, it is a thing that does not exist there, and
 * a greyed-out line saying so would be a promise.
 */
export function recipeOffMap(recipe: Recipe, mapId: string): boolean {
  return recipe.onlyOn !== undefined && recipe.onlyOn !== mapId;
}

/** The recipes that exist on this map. */
export function recipesOn(mapId: string): readonly Recipe[] {
  return RECIPES.filter((r) => !recipeOffMap(r, mapId));
}

/** The recipe this grid currently satisfies, if any. */
export function matchRecipe(grid: Inventory, mapId = ""): Recipe | null {
  for (const recipe of RECIPES) {
    // Off-map recipes do not match at all, or a танк could be laid out on
    // the кремль by somebody who knew the shape.
    if (mapId.length > 0 && recipeOffMap(recipe, mapId)) continue;
    const ok = recipe.kind === "shaped"
      ? matchesShaped(recipe, grid)
      : matchesShapeless(recipe, grid);
    if (ok) return recipe;
  }
  return null;
}

/**
 * A recipe the grid holds the makings of but has not laid out correctly.
 *
 * The grid counts *occupied slots*, not items: three stalks stacked in one
 * cell is not the fibre recipe, three stalks in three cells is. That rule is
 * deliberate and worth keeping - a grid holding four stacks of clay crafts a
 * brick per click without being refilled - but nothing said it out loud, and
 * an unmatched grid looks exactly like a broken game.
 *
 * So when nothing matches, this asks the softer question: is everything here
 * wanted by some one recipe, and is there enough of it? If so the player has
 * the right things in the wrong arrangement, and the UI can say which recipe
 * they were reaching for instead of showing an empty slot.
 *
 * Returns null when the grid matches a recipe outright, when it is empty, or
 * when more than one recipe fits - guessing between two is worse than silence.
 */
export function nearMiss(grid: Inventory): Recipe | null {
  if (matchRecipe(grid) !== null) return null;

  const have = new Map<number, number>();
  for (let i = 0; i < GRID_SLOTS; i++) {
    const slot = grid.get(i);
    if (slot === null) continue;
    have.set(slot.id, (have.get(slot.id) ?? 0) + slot.count);
  }
  if (have.size === 0) return null;

  const exact: Recipe[] = [];
  const enough: Recipe[] = [];

  for (const recipe of RECIPES) {
    const cost = recipeCost(recipe);

    // Nothing in the grid may be foreign to the recipe, or "you have the
    // makings" is not true - a stray block is a different mistake.
    const wanted = new Set(cost.flatMap(({ ingredient }) => ingredientItems(ingredient)));
    if ([...have.keys()].some((id) => !wanted.has(id))) continue;

    let fits = true;
    let same = true;
    for (const { ingredient, count } of cost) {
      let total = 0;
      for (const id of ingredientItems(ingredient)) total += have.get(id) ?? 0;
      if (total < count) fits = false;
      if (total !== count) same = false;
    }
    if (!fits) continue;
    (same ? exact : enough).push(recipe);
  }

  /*
   * Prefer the recipe whose amounts match exactly.
   *
   * Four lumps of clay are enough for a brick and enough for a pail, so "has
   * enough" alone is ambiguous for most of the clay recipes and the hint would
   * never appear for any of them. Exactly four is the brick and nothing else.
   * Ambiguity that survives that is left silent: guessing between two is worse
   * than saying nothing.
   */
  if (exact.length === 1) return exact[0]!;
  if (exact.length === 0 && enough.length === 1) return enough[0]!;
  return null;
}

/**
 * Consume one item from every occupied grid slot.
 *
 * One per slot, not one stack: a grid holding four stacks of ten clay crafts
 * four bricks and leaves nine clay in each slot, which is what every block
 * game does and what players expect.
 */
export function consumeIngredients(grid: Inventory): void {
  for (let i = 0; i < GRID_SLOTS; i++) {
    if (grid.get(i) !== null) grid.takeFromSlot(i, 1);
  }
}

/** Does this inventory hold enough to make the recipe at all? */
export function canCraft(recipe: Recipe, inventory: Inventory): boolean {
  for (const { ingredient, count } of recipeCost(recipe)) {
    let have = 0;
    for (const id of ingredientItems(ingredient)) have += inventory.countOf(id);
    if (have < count) return false;
  }
  return true;
}

/** Tip everything in the grid back into the inventory. Returns what did not fit. */
export function clearGridInto(grid: Inventory, inventory: Inventory): number {
  let lost = 0;
  for (let i = 0; i < GRID_SLOTS; i++) {
    const slot = grid.get(i);
    if (slot === null) continue;
    lost += inventory.add(slot.id, slot.count);
    grid.set(i, null);
  }
  return lost;
}

/**
 * Lay a recipe out in the grid, taking the items from the inventory.
 *
 * This is what makes the recipe book more than documentation: click an entry
 * and the grid fills itself. Returns false and changes nothing if the items
 * are not all there.
 */
/**
 * Lay another course of the same recipe over what is already in the grid.
 *
 * The grid has always counted *occupied slots* rather than items, so a grid
 * stacked four deep in the right shape crafts four times over without being
 * refilled - that is a deliberate old rule and it is what makes this worth
 * doing. What was missing was a way to stack it without dragging: clicking a
 * recipe laid out exactly one of each and clicking again laid out exactly one
 * of each, so making ten тинів was ten trips to the book and ten to the
 * result slot. "Зроби що б клік на рецепт збільшував на 1 шаг кількість
 * інгредієнтів поки їх вистачає в інвентарі."
 *
 * Each cell is topped up with **its own item**, not merely with something
 * that satisfies the ingredient: a cell of oak planks and a bag of pine ones
 * would otherwise be asked to hold two different ids in one stack.
 *
 * Everything is checked before anything is taken, so a click that cannot
 * afford a whole course changes nothing at all. A half-laid course would
 * still craft - the first time - and then fail on whichever cell ran out
 * first, which is a worse thing to hand somebody than a click that did
 * nothing.
 */
export function addBatchToGrid(
  recipe: Recipe,
  inventory: Inventory,
  grid: Inventory,
): boolean {
  // Only onto its own recipe: adding a course to somebody else's layout
  // would be making a mess out of two.
  if (matchRecipe(grid)?.id !== recipe.id) return false;

  const cells: number[] = [];
  const need = new Map<number, number>();
  for (let i = 0; i < GRID_SLOTS; i++) {
    const cell = grid.get(i);
    if (cell === null) continue;
    if (cell.count + 1 > itemMaxStack(cell.id)) return false;
    cells.push(i);
    need.set(cell.id, (need.get(cell.id) ?? 0) + 1);
  }
  if (cells.length === 0) return false;
  for (const [id, count] of need) {
    if (inventory.countOf(id) < count) return false;
  }

  for (const i of cells) {
    const cell = grid.get(i)!;
    let taken = 0;
    for (let slot = 0; slot < inventory.slots.length && taken === 0; slot++) {
      const held = inventory.get(slot);
      if (held === null || held.id !== cell.id) continue;
      taken = inventory.takeFromSlot(slot, 1);
    }
    // The count above said this cannot happen; if it ever does, stop rather
    // than write a cell that nothing was paid for.
    if (taken === 0) return false;
    grid.set(i, { id: cell.id, count: cell.count + 1 });
  }
  return true;
}

export function fillGrid(
  recipe: Recipe,
  inventory: Inventory,
  grid: Inventory,
): boolean {
  if (!canCraft(recipe, inventory)) return false;

  // Whatever is already in the grid goes back first, so its contents count
  // toward what we have.
  clearGridInto(grid, inventory);
  if (!canCraft(recipe, inventory)) return false;

  /** Take one item satisfying `ing`, returning which one it was. */
  const take = (ing: Ingredient): number | null => {
    for (let i = 0; i < inventory.slots.length; i++) {
      const slot = inventory.get(i);
      if (slot === null || !ingredientMatches(slot.id, ing)) continue;
      if (inventory.takeFromSlot(i, 1) === 1) return slot.id;
    }
    return null;
  };

  if (recipe.kind === "shaped") {
    for (let r = 0; r < recipe.pattern.length; r++) {
      const row = recipe.pattern[r]!;
      for (let c = 0; c < row.length; c++) {
        const ch = row[c]!;
        if (ch === " ") continue;
        const ing = recipe.key[ch];
        if (ing === undefined) return false;
        const taken = take(ing);
        if (taken === null) return false;
        grid.set(r * GRID_SIZE + c, { id: taken, count: 1 });
      }
    }
    return true;
  }

  let slot = 0;
  for (const ing of recipe.ingredients) {
    for (let n = 0; n < ing.count; n++) {
      const taken = take(ing.id);
      if (taken === null) return false;
      grid.set(slot++, { id: taken, count: 1 });
    }
  }
  return true;
}

/**
 * Take the crafted result onto the cursor.
 *
 * Returns the new cursor contents, or the old one unchanged when the result
 * cannot be picked up - a different item in hand, or no room for more.
 */
export function takeResult(
  grid: Inventory,
  held: ItemStack | null,
  stations: ReadonlySet<Station> = new Set(),
): ItemStack | null {
  const recipe = matchRecipe(grid);
  if (recipe === null) return held;
  // Nothing is consumed when the fire is missing.
  if (recipeBlocked(recipe, stations) !== null) return held;

  const { id, count } = recipe.result;

  if (held === null) {
    consumeIngredients(grid);
    return { id, count };
  }
  if (held.id !== id || held.count + count > itemMaxStack(id)) return held;

  consumeIngredients(grid);
  return { id, count: held.count + count };
}
